import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { MomentosProvider } from "@/context/MomentosContext";
import { Toaster } from "@/components/ui/sonner";
import Layout from "@/components/Layout";
import track from "@/lib/track";

import Login from "@/pages/Login";
import Registro from "@/pages/Registro";
import Recuperar from "@/pages/Recuperar";
import Invitacion from "@/pages/Invitacion";
import Bienvenida from "@/pages/Bienvenida";
import Conocerte from "@/pages/Conocerte";
import Boot from "@/pages/Boot";
import Inicio from "@/pages/Inicio";
import Perfil from "@/pages/Perfil";
import Mesa from "@/pages/Mesa";
import Diagnostico from "@/pages/Diagnostico";
import Proceso from "@/pages/Proceso";
import Implementar from "@/pages/Implementar";
import Procesos from "@/pages/Procesos";
import MiEmpresa from "@/pages/MiEmpresa";
import Operacion from "@/pages/Operacion";
import MiTiempo from "@/pages/MiTiempo";
import Admin from "@/pages/Admin";
import TratoAdaptador from "@/components/TratoAdaptador";
import BienvenidaVideo from "@/pages/BienvenidaVideo";
import OAuthAuthorize from "@/pages/OAuthAuthorize";
import { Privacidad, Terminos, Soporte } from "@/pages/Legal";

function Cargando() {
  return (
    <div className="min-h-screen bg-negro flex items-center justify-center">
      <div className="w-14 h-14 rounded-full grad-dorado glow-dorado animate-breathe" />
    </div>
  );
}

function Protegida({ children }) {
  const { user } = useAuth();
  if (user === undefined) return <Cargando />;
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

// ¿La cuenta completó las 4 preguntas de /conocerte (empresa + rol)?
// El equipo (admin/closer) nunca hace este recorrido.
function onboardingCompleto(user, empresa) {
  if (!user) return false;
  if (user.rol === "admin" || user.rol === "closer") return true;
  return Boolean(
    (empresa?.nombre || "").trim() &&
    (empresa?.rubro || "").trim() &&
    (empresa?.tamano || "").trim() &&
    (user?.rol_en_empresa || "").trim()
  );
}

// ¿La cuenta terminó la preparación de /perfil (nivel técnico + qué computadora usa)?
// Es una bandera aparte de perfil_listo: perfil_listo significa "el perfil se extrajo",
// esto significa "la persona ya contestó las dos preguntas del final del recorrido".
function preparacionCompleta(user) {
  if (!user) return false;
  if (user.rol === "admin" || user.rol === "closer") return true;
  return Boolean((user?.nivel_tecnologico || "").trim() && (user?.sistema_operativo || "").trim());
}

// Control de acceso al recorrido: mientras falten las 4 respuestas, cae en /conocerte.
// Vale para navegar dentro de la app, escribir la URL a mano y el botón atrás.
function Recorrido({ children }) {
  const { user, empresa } = useAuth();
  if (user === undefined) return <Cargando />;
  if (!user) return <Navigate to="/login" replace />;
  if (!onboardingCompleto(user, empresa)) return <Navigate to="/conocerte" replace />;
  return children;
}

// Rutas de ADENTRO: además del onboarding, exigen la preparación de /perfil.
// Si faltan las 4 preguntas -> /conocerte; si faltan nivel/sistema operativo -> /perfil.
function RecorridoAdentro({ children }) {
  const { user, empresa } = useAuth();
  if (user === undefined) return <Cargando />;
  if (!user) return <Navigate to="/login" replace />;
  if (!onboardingCompleto(user, empresa)) return <Navigate to="/conocerte" replace />;
  if (!preparacionCompleta(user)) return <Navigate to="/perfil" replace />;
  return children;
}

function ConLayout({ children }) {
  return <RecorridoAdentro><Layout>{children}</Layout></RecorridoAdentro>;
}

// Registra pantalla_vista en cada cambio de ruta (solo si hay sesión).
function RouteTracker() {
  const { user } = useAuth();
  const loc = useLocation();
  useEffect(() => {
    if (user) track("pantalla_vista", { ruta: loc.pathname });
  }, [loc.pathname, user]);
  return null;
}

// Video de bienvenida: se muestra en su propia ruta (/bienvenida-video), después de /perfil
// y antes de la mesa. Ver pages/BienvenidaVideo.jsx. El re-ver manual vive en el menú de la cuenta.

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <MomentosProvider>
        <RouteTracker />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/registro" element={<Registro />} />
          <Route path="/recuperar" element={<Recuperar />} />
          <Route path="/invitacion/:token" element={<Invitacion />} />
          <Route path="/oauth/authorize" element={<OAuthAuthorize />} />
          <Route path="/privacidad" element={<Privacidad />} />
          <Route path="/terminos" element={<Terminos />} />
          <Route path="/soporte" element={<Soporte />} />

          <Route path="/bienvenida" element={<Protegida><Bienvenida /></Protegida>} />
          <Route path="/conocerte" element={<Protegida><Conocerte /></Protegida>} />
          <Route path="/boot" element={<Protegida><Boot /></Protegida>} />
          <Route path="/inicio" element={<Recorrido><Inicio /></Recorrido>} />
          <Route path="/perfil" element={<Recorrido><Perfil /></Recorrido>} />
          <Route path="/bienvenida-video" element={<Protegida><BienvenidaVideo /></Protegida>} />
          <Route path="/diagnostico" element={<RecorridoAdentro><Diagnostico /></RecorridoAdentro>} />
          <Route path="/proceso/:id" element={<RecorridoAdentro><Proceso /></RecorridoAdentro>} />
          <Route path="/implementar/:id" element={<RecorridoAdentro><Implementar /></RecorridoAdentro>} />
          <Route path="/admin" element={<Protegida><Admin /></Protegida>} />

          <Route path="/" element={<ConLayout><Mesa /></ConLayout>} />
          <Route path="/procesos" element={<ConLayout><Procesos /></ConLayout>} />
          <Route path="/mi-empresa" element={<ConLayout><MiEmpresa /></ConLayout>} />
          <Route path="/operacion" element={<ConLayout><Operacion /></ConLayout>} />
          <Route path="/mi-tiempo" element={<ConLayout><MiTiempo /></ConLayout>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        </MomentosProvider>
      </BrowserRouter>
      <TratoAdaptador />
      <Toaster position="top-center" />
    </AuthProvider>
  );
}

export default App;
